<template>
  <div class="material-file-manager">
    <!-- 文件上传区域 -->
    <div class="upload-section">
      <Upload
          v-if="props.edit"
          name="file"
          :multiple="multiple"
          :customRequest="handleCustomUpload"
          :fileList="fileList"
          :onChange="handleFileChange"
          :beforeUpload="handleBeforeUpload"
          @remove="handleRemove"
          :showUploadList="false"
      >
        <Button>
          <UploadOutlined /> {{ uploadButtonText }}
        </Button>
      </Upload>

      <!-- 支持格式提示 -->
      <div v-if="props.edit && supportedFormats && supportedFormats.length > 0" class="format-tip">
        支持格式: {{ supportedFormats.join(', ') }}
      </div>
    </div>

    <!-- 已上传文件列表 -->
    <div v-if="fileList.length > 0" class="file-list-section">
      <div class="section-title">已上传文件 ({{ fileList.length }})</div>
      <ul class="file-list">
        <li v-for="file in fileList" :key="file.id" class="file-item">
          <div class="file-info">
            <FileOutlined class="file-icon" />
            <span class="file-name">{{ file.name }}</span>
            <span v-if="file.size" class="file-size">({{ formatFileSize(file.size) }})</span>
          </div>
          <div class="file-actions">
            <a @click="handleFilePreview(file)" class="action-btn preview-btn">
              <EyeOutlined /> 预览
            </a>
            <a v-if="props.edit" @click="handleFileDelete(file)" class="action-btn delete-btn">
              <DeleteOutlined /> 删除
            </a>
          </div>
        </li>
      </ul>
    </div>

    <!-- 重新上传按钮 -->
    <div v-if="fileList.length > 0 && props.edit" class="reupload-section">
      <a-button @click="handleReupload" type="link">
        <ReloadOutlined /> 清空并重新上传
      </a-button>
    </div>

    <!-- 文件预览抽屉 -->
    <FilePreview
        v-model:visible="previewVisible"
        :file-url="previewFileUrl"
        :file-name="previewFileName"
        @close="handlePreviewClose"
    />
  </div>
</template>

<script setup name="MaterialFileManager">
import { ref, defineProps, defineEmits, watch, defineExpose } from 'vue';
import { Upload, Button, message } from 'ant-design-vue';
import {
  UploadOutlined,
  FileOutlined,
  EyeOutlined,
  DeleteOutlined,
  ReloadOutlined
} from '@ant-design/icons-vue';
import FilePreview from '@/components/FilePreview/index.vue';
import { uploadOneFile } from "@/api/upload.js";

// 注入全局权限控制","},{

// 定义props
const props = defineProps({
  // 材料ID
  materialId: {
    type: String,
    required: true
  },
  // 是否允许编辑
  edit: {
    type: Boolean,
    default: true
  },
  // 初始文件列表
  initialFiles: {
    type: Array,
    default: () => []
  },
  // 是否支持多文件上传
  multiple: {
    type: Boolean,
    default: true
  },
  // 支持的文件格式
  supportedFormats: {
    type: Array,
    default: () => []
  },
  // 上传按钮文本
  uploadButtonText: {
    type: String,
    default: '选择文件'
  },
  // 最大文件大小（MB）
  maxFileSize: {
    type: Number,
    default: 50
  }
});

// 定义事件
const emit = defineEmits([
  'update:files', // 文件列表更新事件
  'upload-success', // 文件上传成功事件
  'upload-error', // 文件上传失败事件
  'delete', // 文件删除事件
  'before-upload' // 文件上传前的验证事件
]);

// 文件列表状态 - 初始化时按文件名排序
const fileList = ref([...props.initialFiles].sort((a, b) => a.name.localeCompare(b.name, 'zh-CN')));

// 预览相关状态
const previewVisible = ref(false);
const previewFileUrl = ref('');
const previewFileName = ref('');

// 处理文件上传前的验证
const handleBeforeUpload = (file) => {
  // 文件大小验证
  const isLtMaxSize = file.size / 1024 / 1024 < props.maxFileSize;
  if (!isLtMaxSize) {
    message.error(`文件大小不能超过 ${props.maxFileSize}MB`);
    return Upload.LIST_IGNORE;
  }

  // 文件格式验证
  if (props.supportedFormats && props.supportedFormats.length > 0) {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const isFormatSupported = props.supportedFormats.some(format =>
        format.toLowerCase() === fileExtension
    );

    if (!isFormatSupported) {
      message.error(`不支持的文件格式，请上传 ${props.supportedFormats.join(', ')} 文件`);
      return Upload.LIST_IGNORE;
    }
  }

  // 触发自定义验证事件
  const validationResult = emit('before-upload', file);
  if (validationResult === false) {
    return Upload.LIST_IGNORE;
  }

  return true;
};

// 自定义文件上传处理
// 文件体转化
const convertFormData = (data) => {
  let folder = Date.now().toString();
  const json = {
    bucketName: "testing-management-private",
    pathName: 'material/' + folder, // 材料上传路径
  };
  const formData = new FormData();
  formData.append("uploadFile", data);
  formData.append(
      "json",
      new Blob([JSON.stringify(json)], { type: "application/json" })
  );
  return formData;
};

const handleCustomUpload = async ({ file, onSuccess, onError }) => {
  try {
    // 使用uploadOneFile进行文件上传
    const response = await uploadOneFile(convertFormData(file));
    
    // 上传成功处理
    const uploadedFile = {
      id: file.id,
      name: file.name,
      status: 1,
      size: file.size,
      url: response.data, // 服务端返回的文件URL
      response: { success: true }
    };

    onSuccess(uploadedFile);
    console.info(uploadedFile, "uploadedFile");
    emit('upload-success', uploadedFile, props.materialId);
    message.success(`${file.name} 上传成功`);
  } catch (error) {
    console.error('文件上传失败:', error);
    onError(error);
    emit('upload-error', error, file, props.materialId);
    message.error('文件上传失败');
  }
};

// 处理文件变化
const handleFileChange = ({ fileList: newFileList }) => {
  // 按文件名排序文件列表
  fileList.value = [...newFileList].sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
  emit('update:files', props.materialId, fileList.value);
};

// 处理文件预览
const handlePreview = (file) => {
  if (file && file.url) {
    previewFileUrl.value = file.url;
    previewFileName.value = file.name || '文件预览';
    previewVisible.value = true;
  }
};

// 处理文件删除
const handleRemove = (file) => {
  console.log('删除文件:', file.name);
  emit('delete', file, props.materialId);
  return true;
};

// 处理文件点击预览
const handleFilePreview = (file) => {
  handlePreview(file);
};

// 处理文件点击删除
const handleFileDelete = (file) => {
  // 删除文件后保持排序
  fileList.value = fileList.value.filter(item => item.id !== file.id);
  emit('delete', file, props.materialId);
  emit('update:files', props.materialId, fileList.value);
  message.success('文件已删除');
};

// 处理重新上传
const handleReupload = () => {
  fileList.value = [];
  emit('update:files', props.materialId, fileList.value);
  message.success('已清空所有文件，可以重新上传');
};

// 关闭预览
const handlePreviewClose = () => {
  previewVisible.value = false;
};

// 格式化文件大小
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// 暴露方法给父组件
defineExpose({
  // 获取当前文件列表
  getFileList: () => fileList.value,
  // 清空文件列表
  clearFiles: () => {
    fileList.value = [];
    emit('update:files', props.materialId, fileList.value);
  },
  // 手动添加文件
  addFile: (file) => {
    const newFile = {
      id: Date.now() + Math.floor(Math.random() * 1000),
      name: file.name || '文件',
      status: 1,
      url: file.url || '',
      size: file.size || 0,
      response: { success: true }
    };
    // 添加文件后重新排序
    fileList.value = [...fileList.value, newFile].sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
    emit('update:files', props.materialId, fileList.value);
    return newFile;
  }
});

// 监听初始文件变化
watch(() => props.initialFiles, (newFiles) => {

  if (!newFiles || !Array.isArray(newFiles)) {
    fileList.value = [];
    return;
  }
  // 过滤有效文件并按文件名排序
  const validFiles = newFiles.filter(file => file && typeof file === 'object');
  fileList.value = [...validFiles].sort((a, b) => {
    const nameA = a.name || '';
    const nameB = b.name || '';
    return nameA.localeCompare(nameB, 'zh-CN');
  });
}, { deep: true, immediate: true });

</script>

<style scoped>
.material-file-manager {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 16px;
  background-color: #fff;
}

.upload-section {
  margin-bottom: 16px;
}

.format-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #999;
}

.file-list-section {
  margin-top: 20px;
  border-top: 1px solid #e8e8e8;
  padding-top: 16px;
}

.section-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 12px;
}

.file-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
  transition: background-color 0.2s;
}

.file-item:hover {
  background-color: #fafafa;
}

.file-item:last-child {
  border-bottom: none;
}

.file-info {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.file-icon {
  margin-right: 8px;
  color: #1890ff;
}

.file-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
  color: #333;
}

.file-size {
  font-size: 12px;
  color: #999;
}

.file-actions {
  display: flex;
  gap: 12px;
}

.action-btn {
  font-size: 12px;
  color: #666;
  cursor: pointer;
  transition: color 0.2s;
}

.action-btn:hover {
  color: #1890ff;
}

.preview-btn:hover {
  color: #1890ff;
}

.delete-btn:hover {
  color: #ff4d4f;
}

.reupload-section {
  margin-top: 16px;
  text-align: right;
}

:deep(.ant-upload-list-text) {
  max-height: 200px;
  overflow-y: auto;
}

:deep(.ant-upload-list-item) {
  margin-bottom: 8px;
}
</style>